/**
 * @file services/exportData.js
 * @description Export user data (meals, weight logs) as CSV files.
 */

/**
 * Convert meals array to CSV string.
 * @param {Array} meals
 * @returns {string}
 */
function mealsToCSV(meals) {
  const headers = ["Date", "Description", "Calories (kcal)", "Protein (g)", "Source", "Items"];
  const rows = meals.map((meal) => {
    const date = meal.createdAt?.toDate
      ? meal.createdAt.toDate().toISOString()
      : new Date(meal.createdAt).toISOString();
    const items = (meal.items || []).map((i) => `${i.name} (${i.cal} kcal)`).join("; ");
    return [
      date,
      escapeCsvField(meal.description || ""),
      meal.calories || 0,
      (meal.protein || 0).toFixed(1),
      meal.source || "",
      escapeCsvField(items),
    ].join(",");
  });
  return [headers.join(","), ...rows].join("\n");
}

/**
 * Convert weight logs array to CSV string.
 * @param {Array} weightLogs
 * @returns {string}
 */
function weightLogsToCSV(weightLogs) {
  const headers = ["Date", "Weight (kg)", "Notes"];
  const rows = weightLogs.map((log) => {
    return [
      log.date || "",
      log.weight || 0,
      escapeCsvField(log.notes || ""),
    ].join(",");
  });
  return [headers.join(","), ...rows].join("\n");
}

/**
 * Escape a field for CSV (handle commas, quotes, newlines).
 */
function escapeCsvField(field) {
  const str = String(field);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Trigger a file download in the browser.
 */
function downloadFile(content, filename, mimeType = "text/csv") {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export meals data as CSV.
 * @param {Array} meals
 */
export function exportMealsCSV(meals) {
  const csv = mealsToCSV(meals);
  const date = new Date().toISOString().split("T")[0];
  downloadFile(csv, `dietreg-meals-${date}.csv`);
}

/**
 * Export weight logs as CSV.
 * @param {Array} weightLogs
 */
export function exportWeightCSV(weightLogs) {
  const csv = weightLogsToCSV(weightLogs);
  const date = new Date().toISOString().split("T")[0];
  downloadFile(csv, `dietreg-weight-${date}.csv`);
}

/**
 * Export all data (meals + weight) as separate CSVs in one go.
 * @param {Array} meals
 * @param {Array} weightLogs
 */
export function exportAllData(meals, weightLogs) {
  if (meals.length) exportMealsCSV(meals);
  if (weightLogs.length) exportWeightCSV(weightLogs);
}
